import { useState } from 'react';
import { ApiError, type ApiClient } from '../api/client';
import { useAppServices } from './ViewContext';

/**
 * Preuve téléversée (ADR-054, ADR-064 §5) : empreinte calculée dans le navigateur, demande de
 * téléversement signé, dépôt direct sur le stockage, fin du téléversement, puis `RecordEvidence`
 * rattachée au sujet. Le champ reçoit l'identifiant de la preuve, jamais celui du fichier : c'est la
 * preuve, avec son statut de vérification, que le serveur exige.
 */

/** Contenu d'un fichier ; `FileReader` quand `Blob.arrayBuffer` manque (navigateurs anciens, jsdom). */
function bytesOf(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Lecture du fichier impossible.'));
    reader.readAsArrayBuffer(file);
  });
}

async function sha256(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(await bytesOf(file)));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Dépose un fichier et l'enregistre comme preuve du sujet : empreinte, téléversement signé, fin du
 * téléversement, `RecordEvidence`. Rend l'identifiant de la preuve (partagé avec la caméra du formulaire).
 */
export async function uploadEvidence(client: ApiClient, file: Blob & { name?: string }, subject: { type: string; id: string }, kind?: 'photo' | 'document'): Promise<string> {
  const mediaType = file.type === '' ? 'application/octet-stream' : file.type;
  const requested = await client.command<{ result: { file_id: string; upload: { url: string; method: string; headers?: Record<string, string> } } }>('RequestFileUpload', {
    payload: { file_name: file.name ?? 'preuve', media_type: mediaType, size_bytes: file.size, sha256: await sha256(file) },
  });
  await client.upload(requested.result.upload, file);
  await client.command('CompleteFileUpload', { payload: {}, target: { type: 'File', id: requested.result.file_id } });
  const evidence = await client.command<{ target: { id: string } }>('RecordEvidence', {
    payload: { evidence_kind: kind ?? (mediaType.startsWith('image/') ? 'photo' : 'document'), subject_refs: [subject], file_id: requested.result.file_id, captured_at: new Date().toISOString() },
  });
  return evidence.target.id;
}

export function EvidenceInput({ id, value, onChange, disabled, subject }: { id: string; value: readonly string[]; onChange: (ids: string[]) => void; disabled: boolean; subject: { type: string; id: string } | undefined }) {
  const services = useAppServices();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const add = async (file: File): Promise<void> => {
    if (subject === undefined) return;
    setBusy(true);
    setError(null);
    try {
      onChange([...value, await uploadEvidence(services.client, file, subject)]);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  const blocked = disabled || busy || subject === undefined;
  // Zone de dépôt (maquette du 2026-10-20) : un toucher ouvre l'appareil photo ou le choix du fichier, un
  // fichier glissé dessus s'ajoute ; le champ fichier du navigateur reste dedans pour le clavier.
  return (
    <div className="ay-evidence">
      <label
        className={`ay-evidence__drop${dragging ? ' ay-evidence__drop--over' : ''}${blocked ? ' ay-evidence__drop--disabled' : ''}`}
        onDragOver={(event) => {
          if (blocked) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files[0];
          if (!blocked && file !== undefined) void add(file);
        }}
      >
        <input
          id={id}
          className="ay-evidence__input"
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          disabled={blocked}
          aria-busy={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) void add(file);
            event.target.value = '';
          }}
        />
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 8h3l2-3h6l2 3h3v11H4zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
        </svg>
        <span>
          <strong>{value.length === 0 ? 'Prendre une photo' : 'Ajouter une photo'}</strong> ou déposer un fichier
        </span>
      </label>
      <span className="ay-evidence__status" aria-live="polite">
        {busy ? 'Téléversement…' : value.length === 0 ? '' : value.length === 1 ? '1 preuve jointe' : `${value.length} preuves jointes`}
      </span>
      {error !== null && (
        <span className="ay-field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
